import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Layout/Navbar';
import ImpersonationBanner from './components/ImpersonationBanner';
import Login from './components/Auth/Login';
import Dashboard from './pages/Dashboard';
import History from './pages/History';
import Alerts from './pages/Alerts';
import Account from './pages/Account';
import Activate from './pages/Activate';
import Admin from './pages/Admin';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import OnboardingWizard from './components/Onboarding/OnboardingWizard';
import HelpButton from './components/Help/HelpButton';
import GeofenceSetup from './pages/GeofenceSetup';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="app-loading"><div className="spinner" /></div>;
  return user ? <>{children}</> : <Navigate to="/login" replace />;
}

function RouteGuardedNavbar() {
  const { user } = useAuth();
  return user ? <Navbar /> : null;
}

function AppLayout() {
  const { user } = useAuth();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);

  // Open wizard automatically for new users
  useEffect(() => {
    if (user && !user.onboarding_completed) {
      setWizardStep(1);
      setWizardOpen(true);
    }
  }, [user?.id, user?.onboarding_completed]);

  const openWizard = (step = 1) => {
    setWizardStep(step);
    setWizardOpen(true);
  };

  return (
    <>
      {user && <ImpersonationBanner />}
      <main className="app-main">
        <Routes>
          <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/history" element={<PrivateRoute><History /></PrivateRoute>} />
          <Route path="/alerts" element={<PrivateRoute><Alerts /></PrivateRoute>} />
          <Route path="/account" element={<PrivateRoute><Account /></PrivateRoute>} />
          <Route path="/admin" element={<PrivateRoute><Admin /></PrivateRoute>} />
          <Route path="/login" element={<Login />} />
          <Route path="/activate" element={<Activate />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/geofence/:deviceId" element={<PrivateRoute><GeofenceSetup /></PrivateRoute>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <RouteGuardedNavbar />
      {user && <HelpButton onOpenWizard={openWizard} />}
      {user && wizardOpen && (
        <OnboardingWizard
          startStep={wizardStep}
          onClose={() => setWizardOpen(false)}
        />
      )}
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppLayout />
      </BrowserRouter>
    </AuthProvider>
  );
}
